import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Tooltip,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaCaretDownSolid, FaXmarkRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import { useDropdownDismiss } from '~/hooks/useDropdownDismiss'

export type MultiSelectOption = {
  value: string
  label: string
  /**
   * true の間、 この option は選択肢として**表示したまま選択不可**にする
   * (option 自体を候補から取り除く呼び出し元の絞り込みとは別の軸。
   * `AreaFilterBar` の AC-BSP-005b 対応が最初の利用箇所)。
   */
  disabled?: boolean
  /** `disabled` 時に hover で示す選択不可の理由。 未指定なら Tooltip を出さない。 */
  disabledReason?: string
  /**
   * 選択肢の名前の下 (2 行目) に小さい字で添える補助テキスト (AC-BSP-005b。 置き方は
   * Des 判断 #11325)。 option の名前は `label` のままで、 補助テキストは description として結ぶ。
   */
  description?: string
}

/**
 * chip の × / clear-all の × 用の 20px 角丸アイコンボタン。
 *
 * ecloud `IconButton` の**最小 size は `xs` = 24px** で、 design が要求する 20px
 * (`design/css/components.css` の `.ecl-multi-select__chip-close` = 20x20) に一致する
 * size が無いため custom wrapper とする。
 * (旧コメントは「最小 size は `sm` = 32px」 としていたが誤り。 DS の button recipe は
 *  xs=24 / sm=32 / md=40 / lg=48px。 実測で訂正。)
 *
 * `flexShrink={0}` は必須。 chip が折り返して control が縦に伸びた時、 flex の既定
 * (`flex-shrink: 1`) だと chip 群 (`min-width: auto` で min-content 未満に縮まない)
 * に押されて本ボタンの箱が 0 幅近くまで潰れ、 中の 14px アイコンだけが箱をはみ出して
 * control の枠外へ溢れる (2026-08-12 デザイナー指摘)。 design 側も
 * `.ecl-multi-select__chip-close` / `--typeahead .ecl-multi-select__clear-all` の
 * 双方に `flex-shrink: 0` を持つ (`design/css/components.css`)。
 */
const CloseIconButton = ({
  'aria-label': ariaLabel,
  onClick,
}: {
  'aria-label': string
  onClick: (e: React.MouseEvent) => void
}): React.ReactElement => (
  <Box
    as="button"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    display="inline-flex"
    width="20px"
    height="20px"
    padding={0}
    color="Object.midEmphasis"
    background="transparent"
    border="none"
    borderRadius="full"
    _hover={{ backgroundColor: 'Hover.lowEmphasis' }}
    cursor="pointer"
    aria-label={ariaLabel}
    onClick={onClick}
  >
    <FaXmarkRegular boxSize="14px" />
  </Box>
)

/** portal した menu を control 直下へ置くための viewport 座標 (px)。 */
type MenuRect = {
  /**
   * 下開きは control の下端から `top` で、 上開きは control の上端から `bottom` で
   * 固定する。 上開きを `top` で置くと実高が要るが、 menu は portal なので初回は
   * まだ DOM に無く測れない (`menuRect !== null` が描画条件のため)。 `bottom` 基準なら
   * 高さに依らず control 直上に接するので、 測り直しが要らない。
   */
  top?: number
  bottom?: number
  left: number
  /**
   * control の幅。 menu の**下限**として使う (menu が control より細くならないように)。
   * menu の実幅は候補の中身で決まる (`width: max-content`) — 下記 `maxWidth` 参照。
   */
  width: number
  /** viewport に収まる高さ。 これを超える分は menu 内部でスクロールさせる。 */
  maxHeight: number
  /**
   * viewport に収まる幅。 `width: max-content` の上限。
   *
   * menu は `position: fixed` + `left` 固定なので、 候補が長いと右端を突き抜けて
   * **読めない部分ができる**。 高さを `maxHeight` で viewport に収めるのと同じ考え方で、
   * 幅も収める。 ここに当たった時だけ option の ellipsis が出る。
   */
  maxWidth: number
}

/* menu の余白 / 最小高。 design プロト `js/ecl-multi-select.js:204` と同値。 */
const MENU_GAP = 4
const MENU_EDGE = 8
const MENU_MIN_HEIGHT = 120

type Props = {
  options: MultiSelectOption[]
  /** 選択中の value 配列。 */
  selected: string[]
  /** option 追加 / chip 削除 / 全解除 のたびに新しい配列全体を渡す (= 親が state を完全に管理)。 */
  onChange: (next: string[]) => void
  placeholder?: string
  /** 候補 0 件 (検索前) の menu 表示文言。 */
  emptyText?: string
  /** 検索で候補が 0 件になった時の menu 表示文言。 */
  noMatchText?: string
  disabled?: boolean
  /** label の htmlFor と対応させたい時に typeahead input へ渡す id。 */
  inputId?: string
  /**
   * 幅の決まり方。
   *
   * - `'fill'` (既定): 親の幅いっぱいに広がる。 chip が増えると折り返して**下に伸びる**。
   *   選択数が多くなりうる用途 (承認者選択など「人を複数選ぶ」) 向け。
   * - `'fit'`: 内容に合わせて幅が決まり、 chip は**1 行を維持して右へ伸びる**。
   *   上限は親の幅で、 親 (= wrap する filter 行) が広がれば一緒に広がる。 行に収まら
   *   なくなったらこの部品が縦に伸びるのではなく**フィルタ項目ごと次の行へ折り返す**
   *   のが正 (親側の `flexWrap` が担う)。
   *
   * design は一覧のフィルタ用途にだけ `'fit'` 相当を当てている
   * (`design/demand/cases.html` の `#cases-area-ms` / `#cases-bg-ms` scoped override。
   * 2026-06-15 のコメント「エリア chip は縦に折返さず 1 行維持し、 選択増で右余白へ
   * 伸長、 行の限界で filter-group ごと改行 (需給ギャップ supply-gap と同方針)。
   * スコープ限定で共通コンポーネントの他用途 (人選択等) には影響させない」)。
   * 既定を `'fill'` に置いているのは、 この scoping をそのまま持ち込むため。
   */
  sizing?: 'fill' | 'fit'
  /**
   * 指定すると、 選択不可 (`disabled`) の option を選べる option の後ろへまとめ、 この見出しを
   * 区切り線の上に置く (選べる・選べないを並びでも分ける。 2026-10-30 デザイナー判断、
   * スポット市場のエリア)。 それぞれの中の並びは `options` のまま。 未指定なら `options` の並びのまま
   *
   * [design 差異] 旧 HTML プロトタイプ (`design/js/ecl-multi-select.js`) と React mock にはまだ無い。
   * 2026-10-30 デザイナー指摘「エリアの select リストで、 選択できるものとできないものの視覚差分が少ない」 案 B を
   * dev から先に入れた。 UI 辞書 components/action-reason.md「ツールチップを出せない部品: 選択欄の選択肢」 に
   * 記録 (design update #12667 で main に入る)。 React mock は React mock の集約で後から合わせる
   */
  disabledGroupLabel?: string
  'aria-label': string
}

/**
 * design (`design/demand/cases.html` #case-new-area-ms / #case-new-bg-ms、
 * `design/js/ecl-multi-select.js`、`.ecl-multi-select--typeahead`
 * `design/css/components.css:3264-3303`) 準拠の typeahead multi-select。
 *
 * Chakra v3 (3.29.0) の `Combobox` namespace primitive の型定義は react-19 前提
 * (ecloud-designsystem 0.14.2 の peerDependency は react ^18.2.0) のため、本 app
 * (react-18) の JSX 型と噛み合わず使わない (`~/components/common/FilterMultiSelect.tsx`
 * と同じ制約)。 同ファイルのパターンを踏襲し、 開閉 state を自前管理する Box/Flex
 * ベースの軽量 dropdown として実装する (outside-click / Escape dismiss も自前で持つ)。
 *
 * menu (listbox) は design プロトの共通部品 (`design/js/ecl-multi-select.js` の
 * 「menu は position:fixed なので modal / overflow:auto の中でも見切れない」) と同じく
 * `document.body` へ portal し、 control の viewport 座標に `position: fixed` で
 * 配置する。 absolute 配置のままでは modal body や `PageScrollArea` の
 * overflow でリストが見切れる (issue #5316)。
 *
 * typeahead 検索欄のみ raw `<input>` を使う。 ecloud には文字入力可能な `Input`
 * primitive 自体は存在する (`dist/src/components/index.d.ts` に export 実在) が、
 * 実装は chakra `Field` root (`data-scope="field"`) でラップされ、 1px border +
 * radius + `Surface.primary` 背景 + 固定 height (40px) を持つ**自己完結した箱**として
 * 描画される (実測: レンダー結果の recipe CSS で `border-width:1px` / `border-radius`
 * / `background-color` / `--input-height` が既定付与されることを確認)。 この
 * MultiSelect は chip 群 + 検索欄をまとめて 1 つの外枠 (Flex の `border`) で囲む
 * borderless 埋め込み typeahead が要件のため、 ecloud `Input` をそのまま使うと
 * 二重枠線・二重背景・高さ不整合が生じ、 それを打ち消すための override が本体の
 * デフォルトスタイルと衝突する。 そのため flex 内に flush する raw `<input>` を維持する
 * (同ファイルの対象日 raw `<input type="date">` と同じ理由の既存パターンを踏襲)。
 */
export const MultiSelect = ({
  options,
  selected,
  onChange,
  placeholder,
  emptyText,
  noMatchText,
  disabled = false,
  inputId,
  sizing = 'fill',
  disabledGroupLabel,
  'aria-label': ariaLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [menuRect, setMenuRect] = useState<MenuRect | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()

  // Menu primitive の代替として outside-click / Escape での閉じる挙動を自前で持つ
  // (FilterMultiSelect と同じパターン)。 menu は body へ portal するため、
  // outside 判定には menu 自身も含める。
  useDropdownDismiss(rootRef, isOpen, () => setIsOpen(false), menuRef)

  // menu は body 直下に position:fixed で出すため、 表示のたびに control の
  // viewport 座標を測って追従させる (design プロトの `positionMenu` と同じ)。
  const updateMenuRect = useCallback((): void => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (rect === undefined) return

    /* 高さは **viewport の空きに合わせる**。 固定 max-height だと、 空きが十分でも
       候補が途中で切れる (2026-08-27 デザイナー指摘: 特異日登録のエリア選択で
       10 エリア中 7 件しか出ず、 残りは menu 内スクロールが要る。 実測: 内容 378px を
       280px で clamp)。 逆に空きが足りない時は viewport をはみ出して**下の候補に
       届かなくなる** (position:fixed なのでページを送っても menu が一緒に動く)。

       上開きは「下がほぼ無い」時だけの最後の手段にする。 常に上開きにすると
       sticky なタブバー・ツールバーの裏に潜って結局読めない。
       ロジックは design プロト `js/ecl-multi-select.js` の `positionMenu` と同じ。 */
    const below = window.innerHeight - rect.bottom - MENU_GAP - MENU_EDGE
    const above = rect.top - MENU_GAP - MENU_EDGE
    const openUp = below < MENU_MIN_HEIGHT && above > below
    const avail = Math.max(MENU_MIN_HEIGHT, openUp ? above : below)
    /* 実高は menu 側の `maxHeight` と内容量で決まる。 上開きを `top` で置くと
       「control の上端 − gap − 実高」 の実高が要るが、 menu は portal で
       `menuRect !== null` を描画条件にしているため、 初回オープン時点ではまだ DOM に
       無く `scrollHeight` を測れない (deps も変わらないので描画後に測り直されない)。
       上限いっぱいを仮定すると、 候補が少ない時に menu が control の直上ではなく
       viewport 最上端に貼り付いて大きな空白ができる。
       上開きは `bottom` 基準で control の上端に接地させれば高さに依らないので、
       測定そのものを不要にする。 */
    /* menu は control より**広げてよい** (デザイナー指摘 2026-09-28:
       「BG 選択する前のリストが省略されていて選択しづらい。 未選択の場合でも
       省略表示しない。 list だけ width を広げる (select の width は今のまま)」)。
       control 幅は下限にとどめ、 実幅は候補の中身に任せる。 右端を突き抜けないよう
       viewport までで頭打ちにする。 */
    const maxWidth = Math.max(
      rect.width,
      window.innerWidth - rect.left - MENU_EDGE,
    )
    const next: MenuRect = openUp
      ? {
          bottom: window.innerHeight - rect.top + MENU_GAP,
          left: rect.left,
          width: rect.width,
          maxHeight: avail,
          maxWidth,
        }
      : {
          top: rect.bottom + MENU_GAP,
          left: rect.left,
          width: rect.width,
          maxHeight: avail,
          maxWidth,
        }
    // 座標が変わらない時は同じ object を返して余分な re-render を避ける
    // (scroll listener が高頻度で呼ぶため)。
    setMenuRect((prev) =>
      prev !== null &&
      prev.top === next.top &&
      prev.bottom === next.bottom &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.maxHeight === next.maxHeight &&
      prev.maxWidth === next.maxWidth
        ? prev
        : next,
    )
  }, [])

  // 測定は paint 前 (useLayoutEffect) に行う。 useEffect だと open 直後の 1 フレームを
  // 古い座標で描いてしまう。 chip の追加 / 削除・typeahead 入力で control が折り返して
  // 高さが変わるため、 selected / query も測定トリガに含める。
  useLayoutEffect(() => {
    if (!isOpen || disabled) return
    updateMenuRect()
    // scroll は capture 相で listen して祖先のスクロールコンテナ (modal body /
    // PageScrollArea) の scroll も拾う。
    window.addEventListener('scroll', updateMenuRect, true)
    window.addEventListener('resize', updateMenuRect)
    return () => {
      window.removeEventListener('scroll', updateMenuRect, true)
      window.removeEventListener('resize', updateMenuRect)
    }
  }, [isOpen, disabled, selected, query, updateMenuRect])

  // disabled に変化したら開いている dropdown を強制で閉じる (design `.ecl-multi-select--disabled` 相当)。
  useEffect(() => {
    if (disabled) setIsOpen(false)
  }, [disabled])

  const labelOf = (value: string): string =>
    options.find((o) => o.value === value)?.label ?? value

  // 選択済み value は候補から除外し、 typeahead 入力で label 部分一致絞り込み (小文字化)。
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    return options.filter((o) => {
      if (selected.includes(o.value)) return false
      if (q === '') return true
      return o.label.toLowerCase().includes(q)
    })
  }, [options, selected, query])

  const hasDisabledOption = options.some((opt) => opt.disabled === true)
  const renderOption = (opt: MultiSelectOption): React.ReactNode => {
    const labelId = `${listboxId}-${opt.value}-label`
    const descriptionId = `${listboxId}-${opt.value}-description`
    const hasDescription =
      opt.description !== undefined && opt.description !== ''
    // 選べない option があるリストの、 選べる option (名前を濃く太くする。 下の color のコメント参照)
    const isEmphasized = hasDisabledOption && opt.disabled !== true
    const row = (
      <Box
        key={opt.value}
        textStyle={isEmphasized ? 'label' : undefined}
        // select.md「option」: 0.4rem 0.8rem / 14px、 hover `Hover.accentPrimaryLow`
        overflow="hidden"
        // 選べない option があるリストでは、 選べる名前を濃く太く、 選べない名前を Text.disabled に下げて
        // 名前の濃さと太さで選べるかを分ける (理由は 2 行目が読める濃さで伝える。 旧 HTML プロトタイプの
        // 理由つきの選べない選択肢 #10919 と同じ値。 2026-10-30 デザイナー指摘「視覚差分が少ない」)
        padding="0.4rem 0.8rem"
        color={
          opt.disabled === true
            ? 'Text.disabled'
            : hasDisabledOption
              ? 'Text.highEmphasis'
              : undefined
        }
        fontSize={isEmphasized ? undefined : 'md'}
        _hover={
          opt.disabled
            ? undefined
            : { backgroundColor: 'Hover.accentPrimaryLow' }
        }
        /* `nowrap` は menu の `width: max-content` が 1 行ぶんの幅を
           測れるようにするため (折り返すと max-content が縮む)。
           `ellipsis` は menu が viewport 幅 (`maxWidth`) で頭打ちに
           なった時だけの保険で、 **通常は省略されない**
           (デザイナー指摘 2026-09-28)。 */
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        cursor={opt.disabled ? 'not-allowed' : 'pointer'}
        aria-describedby={hasDescription ? descriptionId : undefined}
        aria-disabled={opt.disabled}
        aria-labelledby={hasDescription ? labelId : undefined}
        onClick={() => {
          if (opt.disabled) return
          handleAdd(opt.value)
        }}
        role="option"
      >
        {hasDescription ? (
          <>
            <Box as="span" display="block" id={labelId}>
              {opt.label}
            </Box>
            <Box
              as="span"
              textStyle="captionNormal"
              display="block"
              color="Text.midEmphasis"
              id={descriptionId}
            >
              {opt.description}
            </Box>
          </>
        ) : (
          opt.label
        )}
      </Box>
    )
    // 理由がある disabled option だけ Tooltip で包む (= 理由が無い
    // 行・選択可能な行を余分な wrapper で汚さない。 `HintTooltip` と
    // 同じ「説明が無ければ付けない」 判断)。 2 行目 (description) がある行は
    // そこで理由を読めるので Tooltip を重ねない (別の文が出て食い違うため。
    // UI 辞書 action-reason.md「選択肢にはツールチップを出さない」)。
    return opt.disabled &&
      !hasDescription &&
      opt.disabledReason !== undefined &&
      opt.disabledReason !== '' ? (
      <Tooltip
        key={opt.value}
        content={opt.disabledReason}
        positioning={{ placement: 'right' }}
      >
        {row}
      </Tooltip>
    ) : (
      row
    )
  }

  const handleRemove = (value: string): void => {
    onChange(selected.filter((v) => v !== value))
  }

  const handleAdd = (value: string): void => {
    if (!selected.includes(value)) onChange([...selected, value])
  }

  const isFit = sizing === 'fit'

  return (
    <Box
      ref={rootRef}
      position="relative"
      // 'fit' は design `#cases-area-ms .ecl-multi-select--typeahead`
      // (`width: fit-content; min-width: 0; max-width: 100%`) の移植。
      width={isFit ? 'fit-content' : '100%'}
      minWidth={0}
      maxWidth="100%"
    >
      <Flex
        alignItems="center"
        gap={1}
        width="100%"
        minHeight="40px"
        padding="4px 8px"
        borderWidth="1px"
        // UI 辞書 dictionary/components.md `.ecl-multi-select` 行 (2026-10-14 / ebalance-prototype#592):
        // 角丸 md (6px)、 開いている間は枠の色を変えず 3px の `Focus.clarity` リングだけ
        // (eclair `--focus-ring-shadow` = `0 0 0 3px var(--focus-clarity)`。 DS Input の `_focus` と同じ)
        borderColor="Border.midEmphasis"
        borderRadius="md"
        boxShadow={
          !disabled && isOpen ? '0 0 0 3px {colors.Focus.clarity}' : undefined
        }
        cursor={disabled ? 'not-allowed' : 'text'}
        transition="border-color 0.12s, box-shadow 0.12s"
        backgroundColor={disabled ? 'Surface.secondary' : 'Surface.primary'}
        onClick={() => {
          if (disabled) return
          inputRef.current?.focus()
        }}
      >
        {/* `minWidth={0}` で min-content 下限 (= typeahead input の 80px) より
            さらに縮めるのを許し、 右端の指標群 (clear-all / divider / caret) を
            押し出さない。 design `.ecl-multi-select--typeahead > __control` の
            `min-width: 0` と同じ意図。 */}
        <Flex
          alignItems="center"
          flexWrap={isFit ? 'nowrap' : 'wrap'}
          flex="1 1 auto"
          gap="6px"
          minWidth={0}
          minHeight="28px"
        >
          {selected.map((value) => (
            /* eslint-disable-next-line ebalance/no-adhoc-typography -- design .ecl-multi-select__chip (components.css:3186-3193) = xs/500 で実装。md と乖離。chip はバッジ・chip 系統合トラックで是正予定 */
            <Flex
              key={value}
              alignItems="center"
              gap={1}
              padding="4px 6px 4px 10px"
              fontSize="md"
              fontWeight="medium"
              borderRadius="sm"
              backgroundColor="Surface.tertiary"
            >
              <Box as="span">{labelOf(value)}</Box>
              {/* disabled 時は「一切操作不可」 を component 自身で保証するため、
                  × ボタン自体を描画せず label のみの chip にする。 */}
              {!disabled && (
                <CloseIconButton
                  aria-label={t('common:multiSelect.removeAriaLabel', {
                    label: labelOf(value),
                  })}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(value)
                  }}
                />
              )}
            </Flex>
          ))}
          <input
            ref={inputRef}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={isOpen}
            aria-label={ariaLabel}
            disabled={disabled}
            id={inputId}
            onChange={(e) => {
              setQuery(e.target.value)
              if (!disabled) setIsOpen(true)
            }}
            onClick={(e) => {
              e.stopPropagation()
            }}
            onFocus={() => {
              if (!disabled) setIsOpen(true)
            }}
            placeholder={selected.length === 0 ? placeholder : undefined}
            role="combobox"
            style={{
              background: 'transparent',
              border: 'none',
              color: LightModeSemanticColors.Text.highEmphasis.value,
              // 'fit' では input が HTML 既定幅 (~200px) のまま縮まないと、
              // 未選択時から不必要に横長になる。 design
              // (`#cases-area-ms ... .ecl-multi-select__search`) と同じく
              // placeholder が見切れない 6.5em まで basis を詰める。
              //
              // [2026-09-29 デザイナー指摘] 選択済みの時に `1 0 80px` (= flex-shrink 0 +
              // minWidth 80px) だと、 chip と並んで幅に入りきらない時に **input が
              // 折り返して control が 2 行になる**。 幅 220px のエリアフィルタでは
              // chip 1 個でも起きていた (chip ~75px + gap 6px + input 80px > 残り ~146px)。
              // 選択済みの input は caret の置き場でしかないので縮めてよい。 これで
              // 「横に詰めていって、入りきらなくなったら縦に伸びる」 になる。
              // 未選択時は placeholder を読ませる必要があるので 80px を維持する。
              flex: isFit
                ? '0 1 auto'
                : selected.length > 0
                  ? '1 1 2em'
                  : '1 0 80px',
              fontFamily: 'inherit',
              fontSize: fontSizes.md.value,
              minWidth: isFit ? '6.5em' : selected.length > 0 ? '2em' : '80px',
              outline: 'none',
              width: isFit ? '6.5em' : undefined,
            }}
            value={query}
          />
        </Flex>
        {selected.length > 0 && !disabled && (
          <CloseIconButton
            aria-label={t('common:multiSelect.deselectAll')}
            onClick={(e) => {
              e.stopPropagation()
              onChange([])
            }}
          />
        )}
        {/* ▼ の左の区切り線は選択の有無によらず常に出す。 全高 1px `Border.midEmphasis`
            (UI 辞書 components/select.md「複数選択」、 DS Select の indicatorSeparator)。
            control の上下 padding (4px) を負の margin で打ち消して枠いっぱいに引く */}
        <Box
          flexShrink={0}
          alignSelf="stretch"
          width="1px"
          backgroundColor="Border.midEmphasis"
          marginY="-4px"
        />
        {/* ▼ は開いても回さない (select.md「▼は開いても回さない(DS は回転しない)」) */}
        <Box
          as="span"
          flexShrink={0}
          display="inline-flex"
          color="Object.midEmphasis"
        >
          {/* design プロト (.ecl-multi-select の caret) の実測は 20px (2026-07-30 実測)。 */}
          <FaCaretDownSolid boxSize={iconSize.labeledButton} />
        </Box>
      </Flex>
      {isOpen &&
        !disabled &&
        menuRect !== null &&
        createPortal(
          <Box
            ref={menuRef}
            position="fixed"
            // modal (chakra zIndex 1400) の前面に出す必要があるため popover (1500) を使う
            // (`DsSelect.tsx` の menuPortal zIndex 1500 と同じ判断)。
            zIndex="popover"
            overflowY="auto"
            /* 座標・寸法は **inline style で渡す**。 Chakra の style props に px 値を
               渡すと 1 ピクセルごとに emotion class が生成され、 scroll 追従で
               毎フレーム別 class が積まれる。 値が連続量なのでここは style が正しい
               (静的な見た目は上の props のまま)。 */
            // select.md「開いた時のリスト」: 枠 Border.lowEmphasis / radius md / 上下 8px / 影 shadSm
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="md"
            boxShadow="shadSm"
            pointerEvents="auto"
            backgroundColor="Surface.primary"
            id={listboxId}
            // DS `Modal` (= ark/Zag Dialog) は open 中 body に inline の
            // `pointer-events: none` を付ける。 body へ portal した menu はこれを継承して
            // クリック不能になるため、 子側で auto に戻す (`DsSelect.tsx` と同じ対処)。
            paddingX={0}
            paddingY={2}
            role="listbox"
            style={{
              // 下開きは top、 上開きは bottom。 どちらか片方だけを渡す
              // (両方渡すと position:fixed では高さが引き伸ばされる)。
              ...(menuRect.top !== undefined
                ? { top: `${menuRect.top}px` }
                : { bottom: `${menuRect.bottom ?? MENU_EDGE}px` }),
              left: `${menuRect.left}px`,
              // control 幅は下限。 実幅は候補の中身で決める (省略させない)。
              minWidth: `${menuRect.width}px`,
              width: 'max-content',
              maxWidth: `${menuRect.maxWidth}px`,
              maxHeight: `${menuRect.maxHeight}px`,
            }}
          >
            {filteredOptions.length === 0 ? (
              <Box
                padding="8px 12px"
                color="Text.midEmphasis"
                fontSize="md"
                textAlign="center"
              >
                {query.trim() !== ''
                  ? (noMatchText ?? t('common:form.noMatch'))
                  : (emptyText ?? t('common:form.noOptions'))}
              </Box>
            ) : (
              <>
                {(disabledGroupLabel === undefined
                  ? filteredOptions
                  : filteredOptions.filter((opt) => opt.disabled !== true)
                ).map(renderOption)}
                {disabledGroupLabel !== undefined &&
                  filteredOptions.some((opt) => opt.disabled === true) && (
                    <Box
                      aria-labelledby={`${listboxId}-disabled-group`}
                      role="group"
                    >
                      {/* 区切り線 + 見出し (12px / 600 / Text.midEmphasis。 押せない行なので hover もカーソルも変えない) */}
                      <Box
                        textStyle="captionStrong"
                        marginTop={1}
                        paddingTop={2}
                        paddingBottom={1}
                        color="Text.midEmphasis"
                        borderColor="Border.lowEmphasis"
                        borderTopWidth="1px"
                        whiteSpace="nowrap"
                        cursor="default"
                        id={`${listboxId}-disabled-group`}
                        paddingX="0.8rem"
                        role="presentation"
                      >
                        {disabledGroupLabel}
                      </Box>
                      {filteredOptions
                        .filter((opt) => opt.disabled === true)
                        .map(renderOption)}
                    </Box>
                  )}
              </>
            )}
          </Box>,
          document.body,
        )}
    </Box>
  )
}
